import { Undo2, Redo2, PanelRight, Film, Ellipsis, MousePointer2, Hand } from 'lucide-react';
import { useCanvasStore } from '../store/canvas-store';
import { usePanelStore } from './panel/panel-store';
import { useTimelineStore } from './timeline/timeline-store';
import { useToolStore } from './tool-store';
import { useReadOnly } from './readonly';

/**
 * Bottom-center floating pill. Quick-access essentials (tools, undo, redo, zoom,
 * editor panel, timeline) plus a ⌘P button opening the command palette. Theme +
 * canvas grid live in Settings; the project name lives top-left by the account
 * blob (ProjectNameChip).
 *
 * Themed via CSS vars (light / dark / system).
 */
export function ActionChip() {
  const undo = useCanvasStore((s) => s.undo);
  const redo = useCanvasStore((s) => s.redo);
  const readOnly = useReadOnly();
  const canUndo = useCanvasStore((s) => s.canUndo) && !readOnly;
  const canRedo = useCanvasStore((s) => s.canRedo) && !readOnly;
  const zoom = useCanvasStore((s) => s.doc.viewport.zoom);
  const panelOpen = usePanelStore((s) => s.open);
  const togglePanel = usePanelStore((s) => s.toggle);
  const tool = useToolStore((s) => s.tool);
  const setTool = useToolStore((s) => s.setTool);
  const timelineOpen = useTimelineStore((s) => s.open);
  const toggleTimeline = useTimelineStore((s) => s.toggle);

  const resetView = () => {
    useCanvasStore.setState((s) => ({
      ...s,
      doc: { ...s.doc, viewport: { pan: { x: 0, y: 0 }, zoom: 1 } },
    }));
  };

  const triggerCmdP = () => {
    window.dispatchEvent(new KeyboardEvent('keydown', { key: 'p', metaKey: true }));
  };

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-0.5 p-1.5 rounded-full text-sm bg-[var(--color-surface)] border border-[var(--color-border)] text-[var(--color-text)] shadow-[0_16px_40px_-18px_rgba(0,0,0,.6)]"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <button
        onClick={() => setTool('select')}
        className={
          'min-h-[40px] px-3 rounded-full font-mono hover:bg-[color-mix(in_oklab,var(--color-text)_8%,transparent)] active:translate-y-px transition-[background,transform] flex items-center ' +
          (tool === 'select' ? 'text-[var(--color-accent)] bg-[var(--color-menu-hover)]' : '')
        }
        title="Select / lasso — drag empty canvas to lasso (V)"
        aria-label="Select tool"
        aria-pressed={tool === 'select'}
      >
        <MousePointer2 size={18} />
      </button>
      <button
        onClick={() => setTool('hand')}
        className={
          'min-h-[40px] px-3 rounded-full font-mono hover:bg-[color-mix(in_oklab,var(--color-text)_8%,transparent)] active:translate-y-px transition-[background,transform] flex items-center ' +
          (tool === 'hand' ? 'text-[var(--color-accent)] bg-[var(--color-menu-hover)]' : '')
        }
        title="Hand / pan — or hold Space (H)"
        aria-label="Hand tool"
        aria-pressed={tool === 'hand'}
      >
        <Hand size={18} />
      </button>
      <span className="w-px h-6 mx-1 bg-[var(--color-border)]" />
      <button
        onClick={undo}
        disabled={!canUndo}
        className="min-h-[40px] px-3 rounded-full font-mono hover:bg-[color-mix(in_oklab,var(--color-text)_8%,transparent)] active:translate-y-px transition-[background,transform] disabled:opacity-30 flex items-center"
        title="Undo (⌘Z)"
        aria-label="Undo"
      >
        <Undo2 size={18} />
      </button>
      <button
        onClick={redo}
        disabled={!canRedo}
        className="min-h-[40px] px-3 rounded-full font-mono hover:bg-[color-mix(in_oklab,var(--color-text)_8%,transparent)] active:translate-y-px transition-[background,transform] disabled:opacity-30 flex items-center"
        title="Redo (⌘⇧Z)"
        aria-label="Redo"
      >
        <Redo2 size={18} />
      </button>
      <span className="w-px h-6 mx-1 bg-[var(--color-border)]" />
      <button
        onClick={resetView}
        className="min-h-[40px] px-3 rounded-full font-mono hover:bg-[color-mix(in_oklab,var(--color-text)_8%,transparent)] active:translate-y-px transition-[background,transform]"
        title="Reset view"
      >
        {Math.round(zoom * 100)}%
      </button>
      <span className="w-px h-6 mx-1 bg-[var(--color-border)]" />
      <button
        onClick={togglePanel}
        className={
          'min-h-[40px] px-3 rounded-full font-mono hover:bg-[color-mix(in_oklab,var(--color-text)_8%,transparent)] active:translate-y-px transition-[background,transform] flex items-center ' +
          (panelOpen ? 'text-[var(--color-tool-code)]' : '')
        }
        title="Toggle editor panel (⌘E)"
        aria-label="Toggle editor panel"
      >
        <PanelRight size={18} />
      </button>
      <button
        onClick={toggleTimeline}
        className={
          'min-h-[40px] px-3 rounded-full font-mono hover:bg-[color-mix(in_oklab,var(--color-text)_8%,transparent)] active:translate-y-px transition-[background,transform] flex items-center ' +
          (timelineOpen ? 'text-[var(--color-accent)]' : '')
        }
        title="Motion timeline (⌘T)"
        aria-label="Toggle motion timeline"
      >
        <Film size={18} />
      </button>
      <span className="w-px h-6 mx-1 bg-[var(--color-border)]" />
      <button
        onClick={triggerCmdP}
        className="min-h-[40px] px-3 rounded-full font-mono hover:bg-[color-mix(in_oklab,var(--color-text)_8%,transparent)] active:translate-y-px transition-[background,transform] flex items-center"
        title="More actions (⌘P)"
        aria-label="More actions"
      >
        <Ellipsis size={18} />
      </button>
    </div>
  );
}
